import { useState, useMemo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Tree } from 'primereact/tree';
import { Button } from 'primereact/button';
import { getRowClassName } from '../utils/common';

export const GroupingForm = forwardRef(({ data, onDownload, groupBy, setGroupBy }, ref) => {
    const previewColumns = useMemo(() => {
        if (data?.preview) return Object.keys(data.preview);
        return [];
    }, [data]);

    const previewRows = useMemo(() => {
        if (data?.preview && previewColumns.length > 0) {
            const rowCount = data.preview[previewColumns[0]]?.length || 0;
            const rows = [];
            for (let i = 0; i < rowCount; i++) {
                const row = {};
                previewColumns.forEach(col => {
                    row[col] = data.preview[col][i];
                });
                rows.push(row);
            }
            return rows;
        }
        return [];
    }, [data, previewColumns]);

    const mappedColumns = useMemo(() => {
        if (!data || !data.mapping) return [];
        return Object.entries(data.mapping)
            .filter(([target, source]) => !!source)
            .map(([target]) => target);
    }, [data]);

    useImperativeHandle(ref, () => ({
        getGroupBy: () => [...groupBy]
    }));

    const moveGroupBy = (col, direction) => {
        const idx = groupBy.indexOf(col);
        if (idx === -1) return;
        const newGroupBy = [...groupBy];
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= groupBy.length) return;
        [newGroupBy[idx], newGroupBy[swapIdx]] = [newGroupBy[swapIdx], newGroupBy[idx]];
        setGroupBy(newGroupBy);
    };

    const treeNodes = useMemo(() => {
        const getNonGroupedColumns = (currentGroupBy) =>
            mappedColumns.filter(col => !currentGroupBy.includes(col));

        if (!groupBy.length || !previewRows.length) {
            return [
                {
                    key: 'all',
                    label: <b>All Data</b>,
                    children: [
                        {
                            key: 'all-table',
                            label: (
                                <DataTable
                                    value={previewRows}
                                    scrollable
                                    showGridlines
                                    style={{
                                        fontSize: '0.9em',
                                        padding: 0,
                                        margin: 0,
                                        lineHeight: 1.2,
                                        minWidth: 'max-content',
                                    }}
                                    rowClassName={getRowClassName}
                                >
                                    {mappedColumns.map(col => (
                                        <Column
                                            key={col}
                                            field={col}
                                            header={
                                                <span
                                                    style={{
                                                        padding: '2px 6px',
                                                        fontSize: '0.9em',
                                                    }}
                                                >
                                                    {col}
                                                </span>
                                            }
                                            style={{
                                                minWidth: 200,
                                                maxWidth: 200,
                                                padding: '1px 2px',
                                            }}
                                            headerStyle={{
                                                padding: '2px 4px',
                                            }}
                                        />
                                    ))}
                                </DataTable>
                            ),
                            leaf: true
                        }
                    ]
                }
            ];
        }

        const groupRecursive = (rows, groupCols, level = 0, parentKey = '') => {
            if (level >= groupCols.length) {
                const visibleCols = getNonGroupedColumns(groupCols);
                return [
                    {
                        key: `${parentKey}-table`,
                        label: (
                            <DataTable
                                value={rows}
                                scrollable
                                showGridlines
                                style={{
                                    fontSize: '0.9em',
                                    padding: 0,
                                    margin: 0,
                                    lineHeight: 1.2,
                                    minWidth: 'max-content',
                                }}
                                rowClassName={getRowClassName(rows)}
                            >
                                {visibleCols.map(col => (
                                    <Column
                                        key={col}
                                        field={col}
                                        header={
                                            <span
                                                style={{
                                                    padding: '2px 6px',
                                                    fontSize: '0.95em',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: 150,
                                                    display: 'block',
                                                }}
                                                title={col}
                                            >
                                                {col}
                                            </span>
                                        }
                                        style={{
                                            minWidth: 150,
                                            maxWidth: 150,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            padding: '2px 4px',
                                        }}
                                        body={rowData => (
                                            <span
                                                style={{
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 150,
                                                    padding: '2px 4px',
                                                }}
                                                title={rowData[col]}
                                            >
                                                {rowData[col]}
                                            </span>
                                        )}
                                        headerStyle={{
                                            padding: '2px 4px',
                                            minWidth: 150,
                                            maxWidth: 150,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    />
                                ))}
                            </DataTable>
                        ),
                        leaf: true
                    }
                ];
            }
            const col = groupCols[level];
            const groups = {};
            rows.forEach(row => {
                const key = row[col] ?? '';
                if (!groups[key]) groups[key] = [];
                groups[key].push(row);
            });
            return Object.entries(groups).map(([key, groupRows], idx) => ({
                key: `${parentKey}${col}:${key}`,
                label: (
                    <span>
                        <b>{col}:</b> {key || <i>(empty)</i>} <span style={{ color: '#888', fontWeight: 400 }}>({groupRows.length})</span>
                    </span>
                ),
                children: groupRecursive(groupRows, groupCols, level + 1, `${parentKey}${col}:${key}||`)
            }));
        };
        const nodes = groupRecursive(previewRows, groupBy);

        const sortNodesAsc = nodes => {
            return nodes
                .slice()
                .sort((a, b) => {
                    if (a.label && b.label) {
                        // If label is a React element, extract text
                        const aText = typeof a.label === 'string'
                            ? a.label
                            : (a.label.props?.children ?? '').toString();
                        const bText = typeof b.label === 'string'
                            ? b.label
                            : (b.label.props?.children ?? '').toString();
                        return aText.localeCompare(bText, undefined, { numeric: true, sensitivity: 'base' });
                    }
                    return 0;
                })
                .map(node => ({
                    ...node,
                    children: node.children ? sortNodesAsc(node.children) : node.children
                }));
        };

        return sortNodesAsc(nodes);
    }, [groupBy, previewRows, mappedColumns]);

    const [expandedKeys, setExpandedKeys] = useState({});
    useEffect(() => {
        const collectKeys = (nodes) => {
            let keys = {};
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    keys[node.key] = true;
                    Object.assign(keys, collectKeys(node.children));
                }
            });
            return keys;
        };
        setExpandedKeys(collectKeys(treeNodes));
    }, [treeNodes]);

    const handleDownload = async () => {
        if (!onDownload) return;

        try {
            await onDownload(groupBy);
        } finally {

        }
    };

    const getSampleRows = () => {
        if (!groupBy.length || !previewRows.length) return [];

        const groupMap = new Map();
        previewRows.forEach(row => {
            const key = groupBy.map(col => row[col]).join('||');
            if (!groupMap.has(key)) {
                groupMap.set(key, []);
            }
            groupMap.get(key).push(row);
        });

        const displayVal = (val) => {
            if (val === undefined || val === null || val === "") return 'n/a';
            return val;
        };

        // Helper function for safe comparison
        const compareValues = (a, b) => {
            // Handle null/undefined/empty values
            const normalizeValue = (val) => {
                if (val === undefined || val === null || val === "") return "";
                return val;
            };

            const normalizedA = normalizeValue(a);
            const normalizedB = normalizeValue(b);

            // If both are numbers, compare numerically
            if (!isNaN(normalizedA) && !isNaN(normalizedB)) {
                return Number(normalizedA) - Number(normalizedB);
            }

            // Otherwise, compare as strings
            const strA = String(normalizedA).toLowerCase();
            const strB = String(normalizedB).toLowerCase();

            if (strA < strB) return -1;
            if (strA > strB) return 1;
            return 0;
        };

        let rows = Array.from(groupMap.values()).map(groupRows => {
            const sampleRow = {};
            groupBy.forEach(col => {
                sampleRow[col] = groupRows[0][col];
            });
            sampleRow.Count = groupRows.length;
            if (groupRows[0].VIN !== undefined) {
                sampleRow.VIN = groupRows[0].VIN;
            }
            mappedColumns
                .filter(col => !groupBy.includes(col) && col !== 'VIN')
                .forEach(col => {
                    const isYearColumn = /year/i.test(col);
                    const values = groupRows.map(r => r[col]).filter(v => {
                        if (v === undefined || v === null || v === '') return false;
                        // For year columns, treat 0 / "0" as empty (backend returns 0 for missing dates)
                        if (isYearColumn && (v === 0 || v === '0')) return false;
                        return true;
                    });
                    if (values.length === 0) {
                        sampleRow[col] = '';
                        return;
                    }
                    let minValue, maxValue;
                    if (values.every(v => !isNaN(Number(v)))) {
                        const nums = values.map(Number);
                        minValue = Math.min(...nums);
                        maxValue = Math.max(...nums);
                    } else {
                        // Use compareValues for consistent string comparison
                        minValue = values.reduce((a, b) => (compareValues(a, b) < 0 ? a : b));
                        maxValue = values.reduce((a, b) => (compareValues(a, b) > 0 ? a : b));
                    }
                    sampleRow[col] = minValue === maxValue ? displayVal(minValue) : `${displayVal(minValue)} - ${displayVal(maxValue)}`;
                });
            return sampleRow;
        });

        // Sort by grouping columns ascending with improved comparison
        rows.sort((a, b) => {
            for (let col of groupBy) {
                const comparison = compareValues(a[col], b[col]);
                if (comparison !== 0) return comparison;
            }
            return 0;
        });

        return rows;
    };

    const availableForGrouping = mappedColumns.filter(col => !groupBy.includes(col));
    return (
        <TabView>
            <TabPanel header="Grouping Columns">
                <div className='grid'>
                    <div className='col-3'>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>Available columns:</div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {availableForGrouping.map(col => (
                                <li key={col} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <Checkbox
                                        inputId={`groupby-available-${col}`}
                                        checked={false}
                                        onChange={() => setGroupBy([...groupBy, col])}
                                    />
                                    <label htmlFor={`groupby-available-${col}`}>{col}</label>
                                </li>
                            ))}
                        </ul>
                        <div style={{ fontWeight: 500, margin: '16px 0 4px 0' }}>Grouping order:</div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {groupBy.map((col, idx) => (
                                <li
                                    key={col}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        marginBottom: 4,
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Checkbox
                                            inputId={`groupby-selected-${col}`}
                                            checked={true}
                                            onChange={() => setGroupBy(groupBy.filter(g => g !== col))}
                                        />
                                        <label htmlFor={`groupby-selected-${col}`}>{col}</label>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Button
                                            icon="pi pi-arrow-up"
                                            className="p-button-text p-button-sm"
                                            disabled={idx === 0}
                                            onClick={() => moveGroupBy(col, 'up')}
                                            style={{ height: 24, width: 24 }}
                                            aria-label="Move up"
                                        />
                                        <Button
                                            icon="pi pi-arrow-down"
                                            className="p-button-text p-button-sm"
                                            disabled={idx === groupBy.length - 1}
                                            onClick={() => moveGroupBy(col, 'down')}
                                            style={{ height: 24, width: 24 }}
                                            aria-label="Move down"
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div
                        className="col-9"
                        style={{
                            height: '62vh',
                            padding: 0,
                            margin: 0,
                            position: 'relative',
                            background: 'transparent',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                width: '100%',
                                height: '100%',
                                overflowX: 'auto',
                                overflowY: 'auto',
                            }}
                        >
                            <Tree
                                value={treeNodes}
                                expandedKeys={expandedKeys}
                                onToggle={e => setExpandedKeys(e.value)}
                                className="p-tree-compact"
                                style={{
                                    fontSize: '0.9em',
                                    padding: 0,
                                    margin: 0,
                                    lineHeight: 1.2,
                                    minWidth: 'max-content',
                                    minHeight: '50vh',
                                }}
                                contentStyle={{
                                    padding: 0,
                                    margin: 0,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </TabPanel>
            <TabPanel
                header={`Grouping Sample (${(() => {
                    // Calculate grouped count
                    if (!groupBy.length || !previewRows.length) return `0 / ${previewRows.length || 0}`;
                    const groupMap = new Map();
                    previewRows.forEach(row => {
                        const key = groupBy.map(col => row[col]).join('||');
                        if (!groupMap.has(key)) {
                            groupMap.set(key, row);
                        }
                    });
                    return `${groupMap.size} / ${previewRows.length}`;
                })()})`}
            >
                <div style={{ marginTop: 12, marginBottom: 12, minHeight: 40, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button
                        label="Download"
                        icon="pi pi-download"
                        className="p-button-success"
                        onClick={handleDownload}
                    />
                </div>
                <div
                    style={{
                        height: '53vh',
                        padding: 0,
                        margin: 0,
                        position: 'relative',
                        background: 'transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '0.9em',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            width: '100%',
                            height: '100%',
                            overflowX: 'auto',
                            overflowY: 'auto',
                        }}
                    >
                        <DataTable
                            value={getSampleRows()}
                            scrollable
                            style={{
                                fontSize: '0.9em',
                                padding: 0,
                                margin: 0,
                                lineHeight: 1.2,
                                minWidth: 'max-content',
                            }}
                            showGridlines
                        >
                            {/* Grouping keys as columns first */}
                            {groupBy.map(col => (
                                <Column
                                    key={col}
                                    field={col}
                                    header={
                                        <span
                                            style={{
                                                padding: '2px 6px',
                                                fontSize: '0.95em',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {col}
                                        </span>
                                    }
                                    style={{
                                        minWidth: 100,
                                        padding: '2px 4px',
                                    }}
                                    headerStyle={{
                                        padding: '2px 4px',
                                    }}
                                />
                            ))}
                            {/* Count column after grouping columns */}
                            <Column
                                key="Count"
                                field="Count"
                                header={
                                    <span
                                        style={{
                                            padding: '2px 6px',
                                            fontSize: '0.95em',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Row Count
                                    </span>
                                }
                                style={{
                                    minWidth: 80,
                                    padding: '2px 4px',
                                    textAlign: 'center',
                                    backgroundColor: '#f0f0f0',
                                }}
                                headerStyle={{
                                    padding: '2px 4px',
                                    textAlign: 'right',
                                }}
                            />
                            {/* VIN column */}
                            {mappedColumns.includes('VIN') && !groupBy.includes('VIN') && (
                                <Column
                                    key="VIN"
                                    field="VIN"
                                    header={
                                        <span
                                            style={{
                                                padding: '2px 6px',
                                                fontSize: '0.95em',
                                            }}
                                        >
                                            VIN
                                        </span>
                                    }
                                    style={{
                                        minWidth: 100,
                                        padding: '2px 4px',
                                    }}
                                    headerStyle={{
                                        padding: '2px 4px',
                                    }}
                                />
                            )}
                            {/* Then the rest of mapped columns (excluding grouping keys and VIN) */}
                            {mappedColumns.filter(col => !groupBy.includes(col) && col !== 'VIN').map(col => (
                                <Column
                                    key={col}
                                    field={col}
                                    header={
                                        <span
                                            style={{
                                                padding: '2px 6px',
                                                fontSize: '0.95em',
                                            }}
                                        >
                                            {col}
                                        </span>
                                    }
                                    style={{
                                        minWidth: 100,
                                        padding: '2px 4px',
                                    }}
                                    headerStyle={{
                                        padding: '2px 4px',
                                    }}
                                />
                            ))}
                        </DataTable>
                    </div>
                </div>
            </TabPanel>
            <TabPanel header={`Mapped data (${previewRows.length})`}>
                <div
                    style={{
                        height: '61vh',
                        padding: 0,
                        margin: 0,
                        position: 'relative',
                        background: 'transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '0.9em',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            width: '100%',
                            height: '100%',
                            overflowX: 'auto',
                            overflowY: 'auto',
                        }}
                    >
                        <DataTable
                            value={previewRows}
                            scrollable
                            style={{
                                fontSize: '0.9em',
                                padding: 0,
                                margin: 0,
                                lineHeight: 1.2,
                                minWidth: 'max-content',
                            }}
                            showGridlines
                            rowClassName={getRowClassName(previewRows)}
                        >
                            {previewColumns.map(col => (
                                <Column
                                    key={col}
                                    field={col}
                                    header={
                                        mappedColumns.includes(col)
                                            ? (
                                                <span
                                                    style={{
                                                        padding: '2px 6px',
                                                        fontSize: '0.95em',
                                                    }}
                                                >
                                                    {col}
                                                </span>
                                            )
                                            : (
                                                <span
                                                    style={{
                                                        background: '#f8d7da',
                                                        color: '#721c24',
                                                        padding: '2px 6px',
                                                        borderRadius: 4,
                                                        fontWeight: 600,
                                                        fontSize: '0.95em',
                                                    }}
                                                >
                                                    {col}
                                                </span>
                                            )
                                    }
                                    style={{
                                        minWidth: 100,
                                        padding: '2px 4px',
                                    }}
                                    headerStyle={{
                                        padding: '2px 4px',
                                    }}
                                />
                            ))}
                        </DataTable>
                    </div>
                </div>
            </TabPanel>
        </TabView>
    );
});

export default GroupingForm;